import { Stack } from "@mui/material";

import { useSortable } from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

import CheckSharpIcon from "@mui/icons-material/CheckSharp";
import QuestionMarkSharpIcon from "@mui/icons-material/QuestionMarkSharp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

function SortableBlockRow({
  block,
  selectedBlock,
  role,
  patientId,
  expandedTrialTypes,
  handleTrialTypeDisplay,
  handleSelectBlock,
  handleBlockChange,
  areTrialTypesEqual,
  setShowTrialTypeModal,
  logUserAction,
  infoData,
  trial,
  surveyData,
  selectedVisit,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    disabled: !!patientId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: "grab",
    borderBottom: "1px solid #bdbdbd",
    padding: "8px",
    color:
      selectedBlock === block.name
        ? "black"
        : block.is_completed
          ? "green"
          : block.roles?.find((role) => role.name === "INV")
            ? "orange"
            : "orange",
    backgroundColor: isDragging ? "rgba(0,0,0,0.03)" : "transparent",
  };

  return (
    <Stack ref={setNodeRef} sx={style} {...attributes} {...listeners}>
      {/* TOP ROW */}
      <Stack direction="row" alignItems={"center"} justifyContent={"space-between"}>
        <div
          onClick={() => {
            logUserAction(
              `Selected eCRF block "${block?.name}" in visit "${selectedVisit?.visit_name}"`,
              `${infoData?.email}`,
              `${trial?.name}`
            );
            handleSelectBlock(block?.name, block?.new_block);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
          }}
        >
          {/* Status icons */}
          {block.is_completed && !block.has_constraint_error && !block.possible_incorrect_data && (
            <CheckSharpIcon
              style={{
                color:
                  block.is_completed &&
                  !block.possible_incorrect_data &&
                  !block.has_constraint_error
                    ? "green"
                    : "green",
                marginRight: "5px",
              }}
            />
          )}

          {block.has_constraint_error && (
            <QuestionMarkSharpIcon style={{ color: "red", marginRight: "5px" }} />
          )}

          {block.possible_incorrect_data && (
            <PriorityHighIcon style={{ color: "red", marginRight: "5px" }} />
          )}

          <span data-ecrf-block={block.name}>{block.name}</span>

          {role === "CRO Admin" && (
            <AddCircleOutlineIcon
              onClick={(e) => {
                e.stopPropagation();
                setShowTrialTypeModal(true);
              }}
              style={{ cursor: "pointer" }}
            />
          )}
        </div>

        {/* Checkbox (not shown for some roles / when patientId is defined) */}
        {patientId === undefined &&
          role !== "Investigator" &&
          role !== "Inv_coordinator" &&
          role !== "Reviewer" && (
            <div className="col-auto">
              <input
                type="checkbox"
                checked={surveyData?.some((selectedBlock) => {
                  return (
                    selectedBlock?.name === block.name &&
                    selectedBlock?.new_block === block?.new_block &&
                    areTrialTypesEqual(selectedBlock, block)
                  );
                })}
                name={block.name}
                onChange={(e) => handleBlockChange(block)}
                className="form-check-input"
                style={{
                  cursor: "pointer",
                }}
                onClick={(e) => e.stopPropagation()} // so clicking checkbox doesn't start drag
              />
            </div>
          )}
      </Stack>

      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}
        sx={{ mt: 0.5 }}
      >
        {/* Trial types */}
        <div style={{ fontSize: "small", color: "black" }}>
          [
          {block.trial_type?.map((blockObject, trialTypeIdx) => {
            if (
              trialTypeIdx < 2 ||
              expandedTrialTypes[block.name] // show all if expanded
            ) {
              return (
                <span
                  key={trialTypeIdx}
                  onClick={() => handleTrialTypeDisplay(block.name)}
                  style={{ cursor: "pointer" }}
                >
                  {blockObject.title || blockObject.label}
                  {trialTypeIdx < block.trial_type.length - 1 && ", "}
                </span>
              );
            }
            return null;
          })}
          {block.trial_type.length > 2 && !expandedTrialTypes[block.name] && (
            <span
              onClick={() => handleTrialTypeDisplay(block.name)}
              style={{
                cursor: "pointer",
                color: "blue",
              }}
            >
              <MoreHorizIcon />
            </span>
          )}
          ]
        </div>

        {/* Roles */}
        <div>
          [
          {block.roles &&
            block.roles?.map((rolesObject, roleIdx) => {
              const isLast = roleIdx === block.roles.length - 1;
              const label = isLast ? rolesObject.name : `${rolesObject.name}, `;
              return (
                <div
                  key={roleIdx}
                  style={{
                    fontSize: "small",
                    display: "inline",
                    color:
                      rolesObject.name === "CRC"
                        ? "red"
                        : rolesObject.name === "INV"
                          ? "orange"
                          : "black",
                  }}
                >
                  {label}
                </div>
              );
            })}
          ]
        </div>
      </Stack>
    </Stack>
  );
}

export default SortableBlockRow;
